import type {InfiniteData, QueryKey} from '@tanstack/react-query';
import Sentry from '@/services/sentry/Sentry';
import queryClient from '@/utils/reactQueryClient';
import {
  CONTENT_SUBMISSION_ANALYSIS_STATUS,
  ContentSubmission,
  ContentSubmissionApproval,
  ContentSubmissionState,
} from '@/types/contentSubmission';
import type {
  ContentSubmissionsListResponse,
  ContentSubmissionsMetaStats,
} from '@/api/fetchers/campaignContentSubmission';
import {
  CAMPAIGN_CONTENT_SUBMISSIONS_QUERY_KEY,
  type ContentSubmissionsQueryKey,
} from '@/hooks/queries/use-campaign-content-submissions-query';
import {CONTENT_SUBMISSION_QUERY_KEY} from '@/hooks/queries/use-content-submission-query';

export interface CacheSnapshot<T = unknown> {
  queryKey: QueryKey;
  previousData: T;
}

function setAnalysisProcessingInListCaches(
  latestVersionId: string
): CacheSnapshot<InfiniteData<ContentSubmissionsListResponse>>[] {
  const snapshots: CacheSnapshot<
    InfiniteData<ContentSubmissionsListResponse>
  >[] = [];

  const entries = queryClient.getQueriesData<
    InfiniteData<ContentSubmissionsListResponse>
  >({queryKey: [CAMPAIGN_CONTENT_SUBMISSIONS_QUERY_KEY]});

  entries.forEach(([queryKey, data]) => {
    if (data == null) {
      return;
    }

    snapshots.push({queryKey, previousData: data});

    queryClient.setQueryData<InfiniteData<ContentSubmissionsListResponse>>(
      queryKey,
      (old) => {
        if (!old) {
          return old;
        }
        return {
          ...old,
          pages: old.pages.map((page) => ({
            ...page,
            data: page.data.map((contentSubmission) =>
              contentSubmission.latestVersionId === latestVersionId
                ? {
                    ...contentSubmission,
                    latestVersionAnalysisStatus:
                      CONTENT_SUBMISSION_ANALYSIS_STATUS.PROCESSING,
                  }
                : contentSubmission
            ),
          })),
        };
      }
    );
  });

  return snapshots;
}

function setAnalysisProcessingInSingleCsCaches(
  latestVersionId: string
): CacheSnapshot<{data: ContentSubmission}>[] {
  const snapshots: CacheSnapshot<{data: ContentSubmission}>[] = [];

  const entries = queryClient.getQueriesData<{data: ContentSubmission}>({
    queryKey: [CONTENT_SUBMISSION_QUERY_KEY],
  });

  entries.forEach(([queryKey, cached]) => {
    const matchesOwnVersion = cached?.data?.versions?.some(
      (v) => v.id === latestVersionId
    );
    const matchesChildVersion = cached?.data?.childContentSubmissions?.some(
      (child) => child.latestVersionId === latestVersionId
    );

    if (!matchesOwnVersion && !matchesChildVersion) {
      return;
    }

    snapshots.push({
      queryKey,
      previousData: cached as {data: ContentSubmission},
    });

    queryClient.setQueryData<{data: ContentSubmission}>(queryKey, (old) => {
      if (!old) {
        return old;
      }
      return {
        ...old,
        data: {
          ...old.data,
          ...(matchesOwnVersion && {
            latestVersionAnalysisStatus:
              CONTENT_SUBMISSION_ANALYSIS_STATUS.PROCESSING,
            versions: old.data.versions?.map((v) =>
              v.id === latestVersionId
                ? {
                    ...v,
                    contentSubmissionAnalysisStatus:
                      CONTENT_SUBMISSION_ANALYSIS_STATUS.PROCESSING,
                  }
                : v
            ),
          }),
          ...(matchesChildVersion && {
            childContentSubmissions: old.data.childContentSubmissions?.map(
              (child) =>
                child.latestVersionId === latestVersionId
                  ? {
                      ...child,
                      latestVersionAnalysisStatus:
                        CONTENT_SUBMISSION_ANALYSIS_STATUS.PROCESSING,
                    }
                  : child
            ),
          }),
        },
      };
    });
  });

  return snapshots;
}

/**
 * Optimistically set `latestVersionAnalysisStatus` to `processing`
 * for a content submission identified by its latestVersionId.
 * Returns snapshots so the caller can restore on mutation error.
 */
export function setAnalysisProcessingInCache(
  latestVersionId: string
): CacheSnapshot[] {
  return [
    ...setAnalysisProcessingInListCaches(latestVersionId),
    ...setAnalysisProcessingInSingleCsCaches(latestVersionId),
  ];
}

/** Maps a ContentSubmissionState to its meta stats key. */
const STATE_TO_META_KEY: Record<
  ContentSubmissionState,
  keyof ContentSubmissionsMetaStats
> = {
  pending: 'totalPendingCount',
  accepted: 'totalAcceptedCount',
  rejected: 'totalRejectedCount',
  archived: 'totalArchivedCount',
};

function nextArchivedFromState(
  toState: ContentSubmissionState,
  fromState: ContentSubmissionState,
  current: string | null | undefined
): string | null | undefined {
  if (toState === 'archived') {
    return fromState;
  }
  if (fromState === 'archived') {
    return null;
  }
  return current;
}

/**
 * Update a content submission in all cached query pages.
 * Accepts a partial object so multiple fields can be updated at once.
 */
export function updateContentSubmissionInCache(
  contentSubmissionId: string,
  updates: Partial<ContentSubmission>
) {
  const entries = queryClient.getQueriesData<
    InfiniteData<ContentSubmissionsListResponse>
  >({
    queryKey: [CAMPAIGN_CONTENT_SUBMISSIONS_QUERY_KEY],
  });

  entries.forEach(([queryKey, data]) => {
    if (data == null) {
      return;
    }

    queryClient.setQueryData<InfiniteData<ContentSubmissionsListResponse>>(
      queryKey,
      (old) => {
        if (!old) {
          return old;
        }

        return {
          ...old,
          pages: old.pages.map((page) => ({
            ...page,
            data: page.data.map((contentSubmission) =>
              contentSubmission.id === contentSubmissionId
                ? {...contentSubmission, ...updates}
                : contentSubmission
            ),
          })),
        };
      }
    );
  });
}

/** Returns all non-null query entries for a campaign, typed. */
function getAllCampaignContentSubmissionEntries(
  campaignId: string
): [QueryKey, InfiniteData<ContentSubmissionsListResponse>][] {
  return queryClient
    .getQueriesData<InfiniteData<ContentSubmissionsListResponse>>({
      queryKey: [CAMPAIGN_CONTENT_SUBMISSIONS_QUERY_KEY, campaignId],
    })
    .filter(([, data]) => data != null) as [
    QueryKey,
    InfiniteData<ContentSubmissionsListResponse>,
  ][];
}

/**
 * Optimistically move a content submission between phases.
 *
 * - Marks the item in the source cache with `previousAasmState` (keeps it in the grid)
 * - Copies the item into the destination cache (prepended to first page)
 * - Updates meta.stats counts in all matching caches
 *
 * Query key structure: [KEY, campaignId, state, {filters}]
 */
export function moveContentSubmissionInCache(
  campaignId: string,
  contentSubmissionId: string,
  fromState: ContentSubmissionState,
  toState: ContentSubmissionState
): void {
  const allEntries = getAllCampaignContentSubmissionEntries(campaignId);

  // Find the item to move from any matching cache
  const movedItem = allEntries
    .flatMap(([, data]) => data.pages.flatMap((page) => page.data) ?? [])
    .find((contentSubmission) => contentSubmission.id === contentSubmissionId);

  if (!movedItem) {
    Sentry.captureMessage(
      `moveContentSubmissionInCache: item ${contentSubmissionId} not found in any cache for campaign ${campaignId}`
    );
    return;
  }

  const fromKey = STATE_TO_META_KEY[fromState];
  const toKey = STATE_TO_META_KEY[toState];
  let addedToDestination = false;

  const updateStats = (stats: ContentSubmissionsMetaStats) => ({
    ...stats,
    [fromKey]: Math.max(0, stats[fromKey] - 1),
    [toKey]: stats[toKey] + 1,
  });

  // Single pass: mark source items, update stats, and prepend to destination.
  allEntries.forEach(([queryKey]) => {
    const queryState = (queryKey as ContentSubmissionsQueryKey)[2];
    const isSource = queryIncludesState(queryState, fromState);
    const isDestination = queryIncludesState(queryState, toState);

    queryClient.setQueryData<InfiniteData<ContentSubmissionsListResponse>>(
      queryKey,
      (old) => {
        if (!old) {
          return old;
        }

        const updatedPages = old.pages.map((page) => ({
          ...page,
          data: isSource
            ? page.data.map((cs) => {
                if (cs.id !== contentSubmissionId) {
                  return cs;
                }
                return {
                  ...cs,
                  aasmState: toState,
                  // Only mark with previousAasmState in single-phase caches,
                  // not in "All" phase — items should always stay visible there.
                  previousAasmState: Array.isArray(queryState)
                    ? undefined
                    : cs.aasmState,
                  archivedFromState: nextArchivedFromState(
                    toState,
                    fromState,
                    cs.archivedFromState
                  ),
                };
              })
            : page.data,
          meta: {...page.meta, stats: updateStats(page.meta.stats)},
        }));

        if (!isDestination) {
          return {...old, pages: updatedPages};
        }

        const firstPage = updatedPages[0];
        if (!firstPage) {
          return {...old, pages: updatedPages};
        }

        const isDuplicate = firstPage.data.some(
          (cs) => cs.id === contentSubmissionId
        );
        if (isDuplicate) {
          return {...old, pages: updatedPages};
        }

        addedToDestination = true;
        const cleanItem = {
          ...movedItem,
          aasmState: toState,
          previousAasmState: undefined,
          archivedFromState: nextArchivedFromState(
            toState,
            fromState,
            movedItem.archivedFromState
          ),
        };

        return {
          ...old,
          pages: [
            {...firstPage, data: [cleanItem, ...firstPage.data]},
            ...updatedPages.slice(1),
          ],
        };
      }
    );
  });

  // If no destination cache existed, invalidate so it fetches fresh on navigation
  if (!addedToDestination) {
    Sentry.captureMessage(
      `moveContentSubmissionInCache: no destination cache found for state "${toState}" in campaign ${campaignId}, invalidating`
    );
    queryClient.invalidateQueries({
      queryKey: [CAMPAIGN_CONTENT_SUBMISSIONS_QUERY_KEY, campaignId, toState],
    });
  }
}

/**
 * Remove all content submissions marked with `previousAasmState` from cache.
 * Called on phase/filter navigation and tab unmount.
 * Stats are already updated by moveContentSubmissionInCache, so only items are removed.
 */
export function cleanupMarkedContentSubmissions(campaignId: string): void {
  const allEntries = getAllCampaignContentSubmissionEntries(campaignId);

  allEntries.forEach(([queryKey]) => {
    queryClient.setQueryData<InfiniteData<ContentSubmissionsListResponse>>(
      queryKey,
      (old) => {
        if (!old) {
          return old;
        }
        const hasMarked = old.pages.some((page) =>
          page.data.some((cs) => cs.previousAasmState != null)
        );
        if (!hasMarked) {
          return old;
        }
        return {
          ...old,
          pages: old.pages.map((page) => ({
            ...page,
            data: page.data.filter((cs) => cs.previousAasmState == null),
          })),
        };
      }
    );
  });
}

function queryIncludesState(
  queryState: unknown,
  targetState: ContentSubmissionState
): boolean {
  return (
    queryState === targetState ||
    (Array.isArray(queryState) && queryState.includes(targetState))
  );
}

export function restoreContentSubmissionsCache(snapshots: CacheSnapshot[]) {
  snapshots.forEach(({queryKey, previousData}) => {
    queryClient.setQueryData(queryKey, previousData);
  });
}

/**
 * Optimistically apply newly submitted department approvals to all caches.
 *
 * Updates three things wherever the content submission appears:
 *   1. `approvalStatus[]`  — marks the approved steps as approved
 *   2. `versions[].approvals` — appends the new approval records to the version
 *   3. `childContentSubmissions[].approvalStatus/versions` — same, for parent CS caches (story frames)
 *
 * The single-CS query should still be invalidated after calling this so that
 * server-assigned IDs and `approvedBy` data are fetched in the background.
 */
function applyVersionApprovals(
  cs: ContentSubmission,
  contentSubmissionVersionId: string,
  newlyApprovedSteps: string[],
  newApprovals: ContentSubmissionApproval[],
  now: string
): ContentSubmission {
  // approvalStatus is a submission-level field derived from the latest version.
  // Only update it when the approved version is the latest one.
  const isLatestVersion = cs.latestVersionId === contentSubmissionVersionId;
  return {
    ...cs,
    ...(isLatestVersion && {
      approvalStatus: cs.approvalStatus?.map((status) =>
        newlyApprovedSteps.includes(status.step)
          ? {...status, approved: true, createdAt: now}
          : status
      ),
    }),
    versions: cs.versions?.map((v) =>
      v.id === contentSubmissionVersionId
        ? {...v, approvals: [...(v.approvals ?? []), ...newApprovals]}
        : v
    ),
  };
}

export function updateVersionApprovalsInCache(
  contentSubmissionId: string,
  contentSubmissionVersionId: string,
  newlyApprovedSteps: string[]
): CacheSnapshot[] {
  if (newlyApprovedSteps.length === 0) {
    return [];
  }

  const snapshots: CacheSnapshot[] = [];
  const now = new Date().toISOString();

  const newApprovals: ContentSubmissionApproval[] = newlyApprovedSteps.map(
    (step) => ({
      // Temporary client-side id — replaced once the background refetch completes.
      id: `optimistic-${step}-${now}`,
      contentSubmissionVersionId,
      step,
      approved: true,
      approvedBy: null,
      createdAt: now,
    })
  );

  const applyToSubmission = (cs: ContentSubmission) =>
    applyVersionApprovals(
      cs,
      contentSubmissionVersionId,
      newlyApprovedSteps,
      newApprovals,
      now
    );

  // ── List caches ──────────────────────────────────────────────────────────────
  const listEntries = queryClient.getQueriesData<
    InfiniteData<ContentSubmissionsListResponse>
  >({queryKey: [CAMPAIGN_CONTENT_SUBMISSIONS_QUERY_KEY]});

  listEntries.forEach(([queryKey, data]) => {
    if (data == null) {
      return;
    }
    snapshots.push({queryKey, previousData: data});
    queryClient.setQueryData<InfiniteData<ContentSubmissionsListResponse>>(
      queryKey,
      (old) => {
        if (!old) {
          return old;
        }
        return {
          ...old,
          pages: old.pages.map((page) => ({
            ...page,
            data: page.data.map((cs) =>
              cs.id === contentSubmissionId ? applyToSubmission(cs) : cs
            ),
          })),
        };
      }
    );
  });

  // ── Single CS caches ─────────────────────────────────────────────────────────
  const singleEntries = queryClient.getQueriesData<{data: ContentSubmission}>({
    queryKey: [CONTENT_SUBMISSION_QUERY_KEY],
  });

  singleEntries.forEach(([queryKey, cached]) => {
    if (cached == null) {
      return;
    }

    const isDirectMatch = cached.data?.id === contentSubmissionId;
    const isParentMatch = cached.data?.childContentSubmissions?.some(
      (child) => child.id === contentSubmissionId
    );

    if (!isDirectMatch && !isParentMatch) {
      return;
    }

    snapshots.push({queryKey, previousData: cached});
    queryClient.setQueryData<{data: ContentSubmission}>(queryKey, (old) => {
      if (!old) {
        return old;
      }

      if (isDirectMatch) {
        return {...old, data: applyToSubmission(old.data)};
      }

      // Parent CS cache (story frames page) — update the matching child.
      return {
        ...old,
        data: {
          ...old.data,
          childContentSubmissions: old.data.childContentSubmissions?.map(
            (child) =>
              child.id === contentSubmissionId
                ? applyToSubmission(child)
                : child
          ),
        },
      };
    });
  });

  return snapshots;
}
